import { APP_INITIALIZER, NgModule } from '@angular/core';
import { ChatInterfaceComponent } from './components/chat-interface/chat-interface.component';
import { BrowserModule } from '@angular/platform-browser';
import { MarkdownModule } from 'ngx-markdown';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SearchComponent } from './components/search/search.component';
import { ProfileComponent } from './components/profile/profile.component';
import { ApplicationComponent } from './components/application/application.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { ApplicationSummaryCardComponent } from './components/application-summary-card/application-summary-card.component';
import { ChatInputComponent } from './components/chat-input/chat-input.component';
import { ChatBubbleComponent } from './components/chat-bubble/chat-bubble.component';
import { CoverLetterComponent } from './components/cover-letter/cover-letter.component';
import { ApplicationsTableComponent } from './components/applications-table/applications-table.component';
import { ToolbarComponent } from './components/toolbar/toolbar.component';
import { ProgressGraphComponent } from './components/progress-graph/progress-graph.component';
import { CoverLetterTableComponent } from './components/cover-letter-table/cover-letter-table.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDividerModule } from '@angular/material/divider';
import { CurriculumVitaeTableComponent } from './components/curriculum-vitae-table/curriculum-vitae-table.component';
import { CurriculumVitaeComponent } from './components/curriculum-vitae/curriculum-vitae.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { TextFieldModule } from '@angular/cdk/text-field';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSidenavModule } from '@angular/material/sidenav';
import { A11yModule } from '@angular/cdk/a11y';
import { FilterPanelComponent } from './components/filter-panel/filter-panel.component';
import { LeftNavigationPanelComponent } from './components/left-navigation-panel/left-navigation-panel.component';
import { HeaderComponent } from './components/header/header.component';
import { UserDetailsComponent } from './components/user-details/user-details.component';
import { SuperDropdownComponent } from './components/shared/super-dropdown/super-dropdown.component';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { DynamicDialogModule, DialogService } from 'primeng/dynamicdialog';
import { GlobalLoadingOverlayComponent } from './components/global-loading-overlay/global-loading-overlay.component';
import { UpgradeModalComponent } from './components/shared/upgrade-modal/upgrade-modal.component';
// import { JobPostingComponent } from './components/job-posting/job-posting.component';
// --- START NEW IMPORTS ---
import { providePrimeNG } from 'primeng/config';
import Lara from '@primeuix/themes/lara';
import { JobDescriptionStepComponent } from './components/job-description-step/job-description-step.component';
import { AnalyticsComponent } from './components/analytics/analytics.component';
// --- END NEW IMPORTS ---
import { authInterceptor } from './services/auth.interceptor';
import { errorNotificationInterceptor } from './services/error-notification.interceptor';
import { AuthService } from './services/auth.service';
import { EntitlementService } from './core/services/entitlement.service';
import { TranslationService } from './services/translation.service';
import { firstValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export function initEntitlements(
  authService: AuthService,
  entitlementService: EntitlementService
): () => Promise<void> {
  return async () => {
    if (!authService.getToken()) {
      // This tab's sessionStorage has no token, but that only means THIS
      // tab never logged in - it does NOT mean there is no session. The
      // httpOnly refresh cookie set on login (AuthService.refreshToken())
      // is shared across every tab of the origin, unlike sessionStorage
      // (per-tab by design - see CLAUDE.md). Without this, a tab opened
      // after logging in elsewhere always fell through to /login, even
      // though the backend session was still perfectly valid.
      //
      // Runs inside an APP_INITIALIZER, so the router holds the initial
      // navigation (and therefore AuthGuard.canActivate) until this
      // resolves - by the time guards/components run, sessionStorage
      // already looks like it would in a tab that had logged in directly.
      const restoredToken = await firstValueFrom(
        authService.refreshToken().pipe(
          map(() => true),
          catchError(() => of(false))
        )
      );
      if (!restoredToken) {
        return;
      }

      // Best-effort: also restore the cached user profile, which is what
      // AuthService.hasStoredSession() checks. A token alone is enough to
      // proceed to entitlements below even if this fails.
      await firstValueFrom(
        authService.refreshUser().pipe(catchError(() => of(null)))
      );
    }

    await firstValueFrom(
      entitlementService.load().pipe(catchError(() => of(void 0)))
    );
  };
}

export function initTranslations(translationService: TranslationService): () => Promise<void> {
  return async () => {
    await translationService.initFromStorage();
  };
}
import { NgxEchartsModule } from 'ngx-echarts';
import * as echarts from 'echarts/core';
import { LineChart, BarChart, PieChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([LineChart, BarChart, PieChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    SearchComponent,
    SuperDropdownComponent,
    MarkdownModule.forRoot(),
    FormsModule,
    ReactiveFormsModule,
    ApplicationsTableComponent,
    ChatInterfaceComponent,
    ToolbarComponent,
    ApplicationSummaryCardComponent,
    ChatInputComponent,
    ChatBubbleComponent,
    CoverLetterComponent,
    CoverLetterTableComponent,
    CurriculumVitaeTableComponent,
    FilterPanelComponent,
    LeftNavigationPanelComponent,
    AppRoutingModule,
    BrowserAnimationsModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSnackBarModule,
    MatDividerModule,
    DragDropModule,
    TextFieldModule,
    MatExpansionModule,
    MatSelectModule,
    MatChipsModule,
    MatCheckboxModule,
    MatSidenavModule,
    A11yModule,
    HeaderComponent,
    UserDetailsComponent,
    ToastModule,
    TooltipModule,
    DynamicDialogModule,
    GlobalLoadingOverlayComponent,
    UpgradeModalComponent,
    CurriculumVitaeComponent,
    ProgressGraphComponent,
    ProfileComponent,
    ApplicationComponent,
    DashboardComponent,
    JobDescriptionStepComponent,
    AnalyticsComponent,
    NgxEchartsModule.forRoot({ echarts }),
  ],
  providers: [
    // Order matters: errorNotificationInterceptor must be OUTER (first) so
    // authInterceptor's silent 401-refresh-and-retry fully resolves before
    // the global error toast ever sees the error - see
    // error-notification.interceptor.ts's own comment for why.
    provideHttpClient(withInterceptors([errorNotificationInterceptor, authInterceptor])),
    MessageService,
    providePrimeNG({
      theme: {
        preset: Lara,
        options: {
          darkModeSelector: '.dark-theme',
        },
      },
      // Raises every PrimeNG overlay (p-dialog, dropdowns, tooltips, ...)
      // above this app's own chrome: the header is z-index 1200/1300
      // (header.component.css) and the floating toolbar is z-index 40
      // (floating-toolbar.component.css). Without this, PrimeNG's default
      // (modal: 1100) sits BELOW the header, so a modal's backdrop dims
      // the page content but leaves the navbar bright on top of it. This
      // is a single global fix - it applies to every current and future
      // p-dialog/overlay in the app, not just one component.
      zIndex: {
        modal: 1400,
        overlay: 1350,
        menu: 1350,
        tooltip: 1350,
      },
    }),
    DialogService,
    {
      provide: APP_INITIALIZER,
      useFactory: initEntitlements,
      deps: [AuthService, EntitlementService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initTranslations,
      deps: [TranslationService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
